import type { CSSProperties, ReactElement } from 'react';
import type { ProposalSummary } from '@shared/api';
import { navigate, proposalPath } from '../../state/router';
import { themeStyle } from '../../lib/theme';
import { coverHeroMode } from '../../pages/momentum-v3/CoverPage';
import { COVER_DEFAULT_IMAGE, isKitAsset, resolveHeroImage } from '../../pages/momentum-v3/assets';
import { displayNameFromUsername } from './AccountMenu';

/** "$12k", "$8.5k", "$1.2M" — the monthly figure as stored, only shortened. */
export function compactMoney(value: number): string {
  const short = (n: number) => (n >= 10 ? Math.round(n).toString() : (Math.round(n * 10) / 10).toString());
  if (value >= 1_000_000) return `$${short(value / 1_000_000)}M`;
  if (value >= 1_000) return `$${short(value / 1_000)}k`;
  return `$${Math.round(value)}`;
}

/** "now", "12m", "3h", "2d", "1w", "4mo". */
export function relativeAge(iso: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

/** "CTV + Search", "CTV, Search +2". */
export function channelsLine(channels: string[]): string | null {
  if (channels.length === 0) return null;
  if (channels.length <= 2) return channels.join(' + ');
  return `${channels.slice(0, 2).join(', ')} +${channels.length - 2}`;
}

const PANEL_BACKGROUND: Record<string, string> = {
  light: 'var(--mv3-light-bg, #f3f4f0)',
  brand: 'var(--doc-primary)',
  dark: 'var(--doc-dark-bg, #1b1e21)',
};

/**
 * A miniature of the deck cover's hero: the brand panel (logo on the cover's
 * panel tint) or the cover photo, chosen by the same coverHeroMode the cover
 * uses. Before a deck exists a found logo still shows on the light panel;
 * with nothing at all, the advertiser's initial.
 */
function Thumb({ item, title }: { item: ProposalSummary; title: string }): ReactElement {
  if (item.workingLabel) {
    return (
      <span className="sidebar-thumb sidebar-thumb-working" aria-hidden="true">
        <span className="sidebar-thumb-spinner" />
      </span>
    );
  }
  const card = item.card;
  const mode = card ? coverHeroMode(card, { coverHero: card.coverHero ?? undefined }) : 'photo';
  if (card && mode !== 'photo') {
    const tint = card.hasCover ? card.panelTint ?? 'light' : 'light';
    const style = { ...themeStyle(card.theme), background: PANEL_BACKGROUND[tint] ?? PANEL_BACKGROUND.light } as CSSProperties;
    return (
      <span className="sidebar-thumb sidebar-thumb-panel" style={style} aria-hidden="true">
        <img
          src={(mode === 'wordmark' ? card.fullLogoUrl : card.logoUrl) ?? undefined}
          alt=""
          className={`sidebar-thumb-logo${tint !== 'light' ? ' sidebar-thumb-logo-plated' : ''}${mode === 'icon' ? ' sidebar-thumb-logo-icon' : ''}`}
        />
      </span>
    );
  }
  if (card?.hasCover) {
    const src = resolveHeroImage({ imageUrl: card.coverImageUrl, fallback: COVER_DEFAULT_IMAGE });
    const kit = isKitAsset(src);
    return (
      <span
        className={`sidebar-thumb${kit ? ' sidebar-thumb-kit' : ''}`}
        style={kit ? (themeStyle(card.theme) as CSSProperties) : undefined}
        aria-hidden="true"
      >
        <img src={src} alt="" className="sidebar-thumb-photo" />
      </span>
    );
  }
  return (
    <span className="sidebar-thumb sidebar-thumb-empty" aria-hidden="true">
      {title.trim().charAt(0).toUpperCase()}
    </span>
  );
}

export function ProposalRow({
  item,
  active,
  entering,
}: {
  item: ProposalSummary;
  active: boolean;
  entering: boolean;
}): ReactElement {
  const title = item.proposalName ?? item.advertiserName ?? item.card?.domain ?? 'Untitled proposal';
  const owner = item.access === 'viewer' && item.ownerUsername ? displayNameFromUsername(item.ownerUsername) : null;
  const meta = [
    owner,
    channelsLine(item.card?.channels ?? []),
    item.card?.monthlyBudget ? `${compactMoney(item.card.monthlyBudget)}/mo` : null,
  ].filter((part): part is string => Boolean(part));

  return (
    <li className={`sidebar-row${entering ? ' sidebar-row-entering' : ''}`}>
      <div className="sidebar-row-inner">
        <button
          type="button"
          className={`sidebar-recent-link${active ? ' sidebar-recent-link-active' : ''}${item.workingLabel ? ' sidebar-recent-link-working' : ''}`}
          title={owner ? `${title} — ${owner} (view only)` : title}
          onClick={() => navigate(proposalPath(item.proposalId))}
        >
          <Thumb item={item} title={title} />
          <span className="sidebar-row-body">
            <span className="sidebar-recent-text">{title}</span>
            {item.workingLabel ? (
              <span className="sidebar-row-status" aria-live="polite">
                {/* Keyed so each new label fades in rather than swapping in place. */}
                <span key={item.workingLabel} className="sidebar-row-status-text">{item.workingLabel.replace(/[.…\s]+$/, '')}…</span>
              </span>
            ) : meta.length > 0 ? (
              <span className="sidebar-row-meta">{meta.join(' · ')}</span>
            ) : null}
          </span>
          {item.workingLabel ? null : <span className="sidebar-row-age">{relativeAge(item.updatedAt)}</span>}
        </button>
      </div>
    </li>
  );
}
